import { useCallback, useEffect, useRef, useState } from 'react';
import { equipSkin, fetchSkinShelf, newlyEarnedNames, unequipSkin } from '../_helpers/skinShelf.js';

/**
 * The skin shelf's data (Skins, mockup 37, ENG-739). Each filter is one read
 * of `GET /me/skins/shelf?category=`, so the list and the counts on the chips
 * always come from the server.
 *
 * Returns `{ category, status, shelf, error, earned, busySkinId, actionError,
 * setCategory, retry, wear, takeOff, dismissEarned }`:
 *   - `status` is 'loading' | 'ready' | 'error' for the current category;
 *     `shelf` stays the last one read while the next loads.
 *   - `earned` lists the names of skins a read just granted (the toast).
 *   - `wear(owned)` / `takeOff(owned)` equip or empty its slot, then re-read.
 *     The boost slot is never empty (ENG-875): taking a boost off makes the
 *     server equip SongShake Boost, which the re-read shows as worn.
 *
 * A change of `refreshKey` reads the shelf again (a purchase confirmed, so a
 * locked FX item is now owned and worn, ENG-816).
 *
 * `onEquipChange(skin, worn)` is called after a wear (`worn` true) or take-off
 * succeeds, with the skin it was for: the stream page switches its vibe set
 * live from it (ENG-812).
 */
export const useSkinShelf = (refreshKey = 0, onEquipChange = null) => {
  const onEquipChangeRef = useRef(onEquipChange);
  onEquipChangeRef.current = onEquipChange;
  const [category, setCategoryState] = useState('all');
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ status: 'loading', shelf: null, error: null });
  const [earned, setEarned] = useState([]);
  const [busySkinId, setBusySkinId] = useState(null);
  const [actionError, setActionError] = useState(null);
  // Which read is current, so a late answer for another filter is dropped.
  const readRef = useRef(0);

  useEffect(() => {
    readRef.current += 1;
    const read = readRef.current;
    setState((current) => ({ status: 'loading', shelf: current.shelf, error: null }));
    fetchSkinShelf(category)
      .then((shelf) => {
        if (readRef.current !== read) return;
        setState({ status: 'ready', shelf, error: null });
        const names = newlyEarnedNames(shelf, shelf.newlyEarned);
        if (names.length) setEarned((current) => [...current, ...names.filter((name) => !current.includes(name))]);
      })
      .catch((error) => {
        if (readRef.current === read) setState((current) => ({ status: 'error', shelf: current.shelf, error }));
      });
  }, [category, attempt, refreshKey]);

  const setCategory = useCallback((next) => {
    setActionError(null);
    setCategoryState(next);
  }, []);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  const run = useCallback((action, owned) => {
    if (!owned?.skin) return;
    const { skin } = owned;
    setBusySkinId(skin.id);
    setActionError(null);
    action(skin)
      .then(() => {
        setAttempt((n) => n + 1);
        try {
          onEquipChangeRef.current?.(skin, action === equipSkin);
        } catch (err) {
          console.error(err);
        }
      })
      .catch((error) => setActionError(error ?? { status: 500 }))
      .finally(() => setBusySkinId(null));
  }, []);
  const wear = useCallback((owned) => run(equipSkin, owned), [run]);
  const takeOff = useCallback((owned) => run(unequipSkin, owned), [run]);
  const dismissEarned = useCallback(() => setEarned([]), []);

  return {
    category,
    status: state.status,
    shelf: state.shelf,
    error: state.error,
    earned,
    busySkinId,
    actionError,
    setCategory,
    retry,
    wear,
    takeOff,
    dismissEarned,
  };
};

export default useSkinShelf;
